import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import PosteScanRaspberry from './PosteScanRaspberry';

function PosteScan() {
  const [lecteur, setLecteur] = useState(null);
  const [session, setSession] = useState(null);
  const [operateur, setOperateur] = useState(null);
  const [operation, setOperation] = useState(null);
  const [cartesScannees, setCartesScannees] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [statistiques, setStatistiques] = useState({
    cartes_aujourd_hui: 0,
    cartes_cette_semaine: 0
  });
  const inputRef = useRef(null);
  const scanTimeoutRef = useRef(null);

  useEffect(() => {
    // Récupérer l'identifiant du lecteur depuis l'URL
    const params = new URLSearchParams(window.location.search);
    const id = params.get('lecteur');
    
    if (id) {
      loadLecteur(id);
    } else {
      setMessage('Configuration requise: Ajoutez ?lecteur=IDENTIFIANT à l\'URL');
      setMessageType('error');
    }
  }, []);

  useEffect(() => {
    // Garder le focus sur l'input
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Charger les statistiques si une session est active
    if (session && operateur) {
      loadStatistiques(operateur.id);
    }

    // Ajouter un listener global pour maintenir le focus et capturer les scans
    const handleGlobalClick = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    const handleKeyDown = (e) => {
      // Si on tape quelque part ailleurs que dans l'input, refocuser
      if (document.activeElement !== inputRef.current && inputRef.current) {
        inputRef.current.focus();
      }
    };

    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [session, operateur]);

  const loadLecteur = async (identifiant) => {
    const { data, error } = await supabase
      .from('lecteurs_codes_barres')
      .select('*')
      .eq('identifiant', identifiant)
      .single();
    
    if (error || !data) {
      setMessage('Lecteur non trouvé');
      setMessageType('error');
    } else {
      setLecteur(data);
    }
  };

  const loadStatistiques = async (operateurId) => {
    const aujourd_hui = new Date();
    aujourd_hui.setHours(0, 0, 0, 0);

    const { data: cartesAujourdhui } = await supabase
      .from('scans_cartes_production')
      .select('*, sessions_scan!inner(*)')
      .eq('sessions_scan.operateur_id', operateurId)
      .gte('heure_scan', aujourd_hui.toISOString());

    const debutSemaine = new Date();
    debutSemaine.setDate(debutSemaine.getDate() - debutSemaine.getDay());
    debutSemaine.setHours(0, 0, 0, 0);

    const { data: cartesSemaine } = await supabase
      .from('scans_cartes_production')
      .select('*, sessions_scan!inner(*)')
      .eq('sessions_scan.operateur_id', operateurId)
      .gte('heure_scan', debutSemaine.toISOString());

    setStatistiques({
      cartes_aujourd_hui: cartesAujourdhui?.length || 0,
      cartes_cette_semaine: cartesSemaine?.length || 0
    });
  };

  const showMessage = (text, type = 'info', duration = 3000) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
    }, duration);
  };

  const processScan = async (scannedCode) => {
    console.log('Code scanné:', scannedCode);
    console.log('Session:', session);
    console.log('Opération:', operation);
    console.log('Opérateur:', operateur);

    if (!scannedCode) {
      console.log('Code vide, ignoré');
      return;
    }

    // Si pas de session active, c'est un scan d'opérateur
    if (!session) {
      await handleOperateurScan(scannedCode);
    } 
    // Si session active mais pas d'opération définie, c'est un scan d'opération
    else if (!operation) {
      await handleOperationScan(scannedCode);
    } 
    // Si opérateur scanne à nouveau sa carte, on termine la session
    else if (scannedCode === operateur.code_barre) {
      await handleTerminerSession();
    }
    // Sinon c'est un scan de carte de production
    else {
      await handleCarteScan(scannedCode);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    
    const scannedCode = scanInput.trim().toUpperCase();
    setScanInput('');

    await processScan(scannedCode);
  };

  const handleInputChange = (value) => {
    console.log('Input change:', value);
    setScanInput(value);

    // Annuler le timeout précédent
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    // Si le champ n'est pas vide, créer un nouveau timeout
    // Les lecteurs de codes-barres tapent très rapidement (>100 caractères/sec)
    // On attend 100ms après le dernier caractère pour considérer que le scan est terminé
    if (value.trim()) {
      scanTimeoutRef.current = setTimeout(() => {
        console.log('Auto-submit après timeout');
        const scannedCode = value.trim().toUpperCase();
        setScanInput('');
        processScan(scannedCode);
      }, 100); // 100ms après le dernier caractère
    }
  };

  const handleOperateurScan = async (codeBarre) => {
    console.log('Recherche opérateur avec code:', codeBarre);
    const { data: operateurData, error } = await supabase
      .from('operateurs')
      .select('*')
      .eq('code_barre', codeBarre)
      .single();

    console.log('Résultat recherche opérateur:', operateurData, error);

    if (error || !operateurData) {
      showMessage('Carte opérateur non reconnue', 'error');
      return;
    }

    setOperateur(operateurData);
    showMessage(`Bonjour ${operateurData.prenom} ${operateurData.nom} ! Scannez l'opération à effectuer.`, 'success', 5000);

    // Créer une session
    const { data: sessionData, error: sessionError } = await supabase
      .from('sessions_scan')
      .insert([{
        operateur_id: operateurData.id,
        lecteur_id: lecteur?.id,
        statut: 'en_cours'
      }])
      .select()
      .single();

    if (!sessionError && sessionData) {
      setSession(sessionData);
    }
  };

  const handleOperationScan = async (codeBarre) => {
    console.log('Recherche opération avec code:', codeBarre);
    const { data: operationData, error } = await supabase
      .from('operations')
      .select('*, types_produits(nom)')
      .eq('code_barre', codeBarre)
      .single();

    console.log('Résultat recherche opération:', operationData, error);

    if (error || !operationData) {
      showMessage('Code opération non reconnu', 'error');
      return;
    }

    // ✅ VÉRIFIER L'HABILITATION DE L'OPÉRATEUR
    console.log('Vérification habilitation opérateur:', operateur.id, 'opération:', operationData.id);
    
    const { data: habilitationCheck, error: habilitationError } = await supabase
      .rpc('peut_effectuer_operation', {
        p_operateur_id: operateur.id,
        p_operation_id: operationData.id
      });
    
    console.log('Résultat vérification habilitation:', habilitationCheck);
    
    if (habilitationError) {
      console.error('Erreur vérification habilitation:', habilitationError);
      showMessage('Erreur lors de la vérification des habilitations', 'error', 5000);
      return;
    }
    
    if (!habilitationCheck) {
      showMessage(`❌ ${operateur.prenom} ${operateur.nom}, vous n'êtes pas habilité(e) pour cette opération : ${operationData.nom}. Contactez votre responsable.`, 'error', 8000);
      return;
    }

    // Vérifier que le lecteur est associé à cette opération
    if (lecteur) {
      const { data: association } = await supabase
        .from('lecteurs_operations')
        .select('*')
        .eq('lecteur_id', lecteur.id)
        .eq('operation_id', operationData.id)
        .single();

      if (!association) {
        showMessage('Cette opération n\'est pas disponible sur ce poste', 'error', 5000);
        return;
      }
    }

    // Mettre à jour la session avec l'opération
    await supabase
      .from('sessions_scan')
      .update({ operation_id: operationData.id })
      .eq('id', session.id);

    setOperation(operationData);
    showMessage(`✓ Opération: ${operationData.nom} (${operationData.types_produits?.nom}). Scannez les cartes de production.`, 'success', 5000);
  };

  const handleCarteScan = async (codeBarre) => {
    const { data: carteData, error } = await supabase
      .from('cartes_production')
      .select('*, types_produits(nom)')
      .eq('code_barre', codeBarre)
      .single();

    if (error || !carteData) {
      showMessage('Carte de production non reconnue', 'error');
      return;
    }

    // Vérifier la correspondance produit
    if (carteData.type_produit_id !== operation.type_produit_id) {
      showMessage(`Erreur: Cette carte est pour "${carteData.types_produits?.nom}" mais l'opération est pour "${operation.types_produits?.nom}"`, 'error', 5000);
      return;
    }

    // Vérifier si déjà scannée
    if (cartesScannees.some(c => c.id === carteData.id)) {
      showMessage('Carte déjà scannée dans cette session', 'error');
      return;
    }

    // Enregistrer le scan
    const { error: scanError } = await supabase
      .from('scans_cartes_production')
      .insert([{
        session_id: session.id,
        carte_production_id: carteData.id
      }]);

    if (scanError) {
      console.error('Erreur enregistrement scan:', scanError);
      showMessage('Erreur lors de l\'enregistrement du scan', 'error');
      return;
    }

    // Ajouter la carte à la liste
    setCartesScannees(prev => [...prev, carteData]);
    
    // Mettre à jour le compteur
    await supabase
      .from('sessions_scan')
      .update({ nombre_cartes_scannees: cartesScannees.length + 1 })
      .eq('id', session.id);

    const carteLabel = carteData.nom_attribue || carteData.code_barre;
    showMessage(`Carte ${carteLabel} enregistrée !`, 'success', 2000);
    loadStatistiques(operateur.id);
  };

  const handleTerminerSession = async () => {
    if (!session) return;

    // Marquer toutes les cartes scannées comme ayant terminé cette opération
    if (cartesScannees.length > 0 && operation) {
      const now = new Date().toISOString();
      
      console.log(`📝 Début validation de ${cartesScannees.length} carte(s) pour l'opération ${operation.nom}`);
      
      for (const carte of cartesScannees) {
        console.log(`🔍 Recherche suivi pour carte ${carte.id} et opération ${operation.id}`);
        
        // Mettre à jour le suivi_operations pour marquer l'opération comme validée
        const { data: suiviExistant, error: selectError } = await supabase
          .from('suivi_operations')
          .select('*')
          .eq('carte_production_id', carte.id)
          .eq('operation_id', operation.id)
          .single();

        console.log('Suivi trouvé:', suiviExistant, 'Erreur:', selectError);

        if (suiviExistant) {
          // Mettre à jour le suivi existant
          const updateData = {
            operateur_id: operateur.id,
            validee: true,
            date_fin: now
          };

          // Ajouter date_debut si elle n'existe pas
          if (!suiviExistant.date_debut) {
            updateData.date_debut = now;
          }

          const { error: updateError } = await supabase
            .from('suivi_operations')
            .update(updateData)
            .eq('id', suiviExistant.id);
          
          console.log('Résultat mise à jour:', { updateError, updateData, suiviId: suiviExistant.id });
          
          if (updateError) {
            console.error('Erreur mise à jour suivi:', updateError);
          } else {
            const carteLabel = carte.nom_attribue || carte.code_barre;
            console.log(`✓ Opération ${operation.nom} validée pour carte ${carteLabel}`);
          }
        } else {
          // Créer un nouveau suivi si il n'existe pas
          console.log('⚠️ Aucun suivi trouvé, création d\'un nouveau');
          
          const { error: insertError } = await supabase
            .from('suivi_operations')
            .insert([{
              carte_production_id: carte.id,
              operation_id: operation.id,
              operateur_id: operateur.id,
              validee: true,
              date_debut: now,
              date_fin: now
            }]);
          
          console.log('Résultat insertion:', insertError);
          
          if (insertError) {
            console.error('Erreur création suivi:', insertError);
          } else {
            const carteLabel = carte.nom_attribue || carte.code_barre;
            console.log(`✓ Nouveau suivi créé pour opération ${operation.nom} sur carte ${carteLabel}`);
          }
        }
      }
      
      console.log('✅ Validation de toutes les cartes terminée');
    }

    // Terminer la session
    await supabase
      .from('sessions_scan')
      .update({
        statut: 'terminee',
        heure_fin: new Date().toISOString()
      })
      .eq('id', session.id);

    showMessage(`Session terminée. ${cartesScannees.length} carte(s) traitée(s). À bientôt ${operateur.prenom} !`, 'success', 5000);

    setTimeout(() => {
      setSession(null);
      setOperateur(null);
      setOperation(null);
      setCartesScannees([]);
    }, 3000);
  };

  const handleAnnulerSession = async () => {
    if (!session || !window.confirm('Voulez-vous vraiment annuler cette session ?')) return;

    await supabase
      .from('sessions_scan')
      .update({
        statut: 'annulee',
        heure_fin: new Date().toISOString()
      })
      .eq('id', session.id);

    showMessage('Session annulée', 'info');

    setSession(null);
    setOperateur(null);
    setOperation(null);
    setCartesScannees([]);
  };

  const getMessageColor = () => {
    switch(messageType) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      default: return '#3b82f6';
    }
  };

  const getStatusText = () => {
    if (!session) {
      return 'Scannez votre carte opérateur pour commencer';
    } else if (!operation) {
      return `Opérateur: ${operateur.prenom} ${operateur.nom} - Scannez l'opération`;
    } else {
      return `Opération: ${operation.nom} - Scannez les cartes de production`;
    }
  };

  // Routing vers la version Raspberry si nécessaire
  if (lecteur?.mode_affichage === 'raspberry') {
    return <PosteScanRaspberry lecteur={lecteur} />;
  }

  // Sinon, affichage classique
  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* En-tête */}
        <div style={{ 
          marginBottom: '30px',
          textAlign: 'center',
          color: 'white'
        }}>
          <h1 style={{ 
            fontSize: '36px',
            fontWeight: '700',
            marginBottom: '10px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            {lecteur?.nom || 'Poste de Scan'}
          </h1>
          <p style={{ fontSize: '16px', opacity: 0.9 }}>
            Système de scan automatique
          </p>
        </div>

        {/* Zone de statut */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          padding: '40px',
          marginBottom: '30px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '22px',
            fontWeight: '600',
            color: '#212529',
            marginBottom: '25px'
          }}>
            {getStatusText()}
          </div>

          {/* Formulaire de scan */}
          <form onSubmit={handleScan}>
            <input
              ref={inputRef}
              type="text"
              value={scanInput}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyPress={(e) => {
                console.log('Key pressed:', e.key, 'Value:', scanInput);
                if (e.key === 'Enter') {
                  console.log('Enter pressed, submitting...');
                  e.preventDefault();
                  
                  // Annuler le timeout si Enter est pressé
                  if (scanTimeoutRef.current) {
                    clearTimeout(scanTimeoutRef.current);
                  }
                  
                  handleScan(e);
                }
              }}
              placeholder="Zone de scan active..."
              autoFocus
              autoComplete="off"
              style={{
                width: '100%',
                maxWidth: '600px',
                padding: '18px 24px',
                border: '3px solid #667eea',
                borderRadius: '12px',
                fontSize: '20px',
                textAlign: 'center',
                outline: 'none',
                fontWeight: '500'
              }}
            />
          </form>

          {/* Message de feedback */}
          {message && (
            <div style={{
              marginTop: '25px',
              padding: '18px 24px',
              backgroundColor: `${getMessageColor()}15`,
              border: `2px solid ${getMessageColor()}`,
              borderRadius: '12px',
              color: getMessageColor(),
              fontSize: '18px',
              fontWeight: '600',
              animation: 'fadeIn 0.3s ease'
            }}>
              {message}
            </div>
          )}
        </div>

        {/* Informations de session active */}
        {session && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
          }}>
            {/* Carte Opérateur */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '25px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                color: '#6c757d',
                fontSize: '13px',
                fontWeight: '700',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Opérateur
              </h3>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#212529',
                marginBottom: '8px'
              }}>
                {operateur.prenom} {operateur.nom}
              </div>
              <div style={{
                color: '#6c757d',
                fontSize: '14px',
                fontFamily: 'monospace'
              }}>
                {operateur.code_barre}
              </div>
            </div>

            {/* Carte Opération */}
            {operation && (
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                padding: '25px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{
                  color: '#6c757d',
                  fontSize: '13px',
                  fontWeight: '700',
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Opération
                </h3>
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#212529',
                  marginBottom: '8px'
                }}>
                  {operation.nom}
                </div>
                <div style={{
                  color: '#6c757d',
                  fontSize: '14px'
                }}>
                  {operation.types_produits?.nom} - Ordre {operation.ordre}
                </div>
              </div>
            )}

            {/* Carte Statistiques */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '25px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                color: '#6c757d',
                fontSize: '13px',
                fontWeight: '700',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Progression
              </h3>
              <div style={{
                fontSize: '42px',
                fontWeight: '900',
                color: '#667eea',
                marginBottom: '8px',
                lineHeight: '1'
              }}>
                {cartesScannees.length}
              </div>
              <div style={{
                color: '#6c757d',
                fontSize: '14px',
                marginBottom: '12px'
              }}>
                carte(s) cette session
              </div>
              <div style={{
                paddingTop: '12px',
                borderTop: '1px solid #e0e5ea',
                fontSize: '13px',
                color: '#6c757d'
              }}>
                Aujourd'hui: {statistiques.cartes_aujourd_hui} | Semaine: {statistiques.cartes_cette_semaine}
              </div>
            </div>
          </div>
        )}

        {/* Liste des cartes scannées */}
        {cartesScannees.length > 0 && (
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{
              color: '#212529',
              fontSize: '22px',
              fontWeight: '700',
              marginBottom: '20px'
            }}>
              Cartes scannées ({cartesScannees.length})
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              {cartesScannees.map((carte, index) => (
                <div key={carte.id} style={{
                  padding: '18px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '10px',
                  border: '2px solid #e0e5ea'
                }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#212529',
                    marginBottom: '6px'
                  }}>
                    #{carte.numero_carte}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6c757d',
                    fontFamily: 'monospace',
                    marginBottom: '10px'
                  }}>
                    {carte.code_barre}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#10b981',
                    fontWeight: '600'
                  }}>
                    ✓ Scanné #{index + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        {session && (
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleTerminerSession}
              style={{
                padding: '18px 48px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: '700',
                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.4)';
              }}
            >
              ✓ Terminer la session
            </button>

            <button
              onClick={handleAnnulerSession}
              style={{
                padding: '18px 48px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: '700',
                boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(239, 68, 68, 0.4)';
              }}
            >
              ✕ Annuler
            </button>
          </div>
        )}

        {/* Instructions */}
        {!session && (
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            marginTop: '30px'
          }}>
            <h3 style={{
              color: '#212529',
              fontSize: '22px',
              fontWeight: '700',
              marginBottom: '20px'
            }}>
              📋 Mode d'emploi
            </h3>
            
            <ol style={{
              color: '#495057',
              fontSize: '16px',
              lineHeight: '1.8',
              paddingLeft: '24px'
            }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Scannez votre carte opérateur</strong> pour vous identifier et démarrer une session
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Scannez le code-barre de l'opération</strong> que vous devez effectuer
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Scannez les codes-barres des cartes de production</strong> au fur et à mesure
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Rescannez votre carte opérateur</strong> pour terminer la session
              </li>
            </ol>

            <div style={{
              marginTop: '24px',
              padding: '18px',
              backgroundColor: '#e3f2fd',
              borderRadius: '10px',
              fontSize: '15px',
              color: '#1976d2'
            }}>
              💡 <strong>Astuce:</strong> Le focus reste automatiquement sur la zone de scan. Scannez simplement vos codes-barres !
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default PosteScan;
